
import { memo, useRef, useEffect, useState } from 'react';
import { ProductCard } from '@/components/ProductCard';
import { ProductCardSkeleton } from '@/components/ProductCardSkeleton';
import { Product } from '@/types/product';

interface ProductGridProps {
  products: Product[];
  isLoading: boolean;
  className?: string;
}

export const ProductGrid = memo(function ProductGrid({ 
  products, 
  isLoading,
  className = "",
}: ProductGridProps) {
  const [visibleItems, setVisibleItems] = useState<Product[]>([]);
  const gridRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    if (!isLoading) {
      // Initially show first batch of products
      setVisibleItems(products.slice(0, 8));
    }
  }, [isLoading, products]);

  useEffect(() => {
    const onScroll = () => {
      if (gridRef.current && visibleItems.length < products.length) {
        const rect = gridRef.current.getBoundingClientRect();
        const isBottomVisible = rect.bottom <= window.innerHeight + 300;
        
        if (isBottomVisible) {
          setVisibleItems(prevItems => {
            const nextBatch = products.slice(0, prevItems.length + 4);
            return nextBatch;
          });
        }
      }
    };

    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [products, visibleItems.length]);

  if (isLoading) {
    return (
      <div className={`grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-5 lg:gap-6 ${className}`}>
        {Array(8).fill(null).map((_, index) => (
          <ProductCardSkeleton key={index} />
        ))}
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="rounded-full bg-muted p-6 mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-8 w-8 text-muted-foreground">
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
        </div>
        <h3 className="text-lg font-semibold">No products found</h3>
        <p className="text-sm text-muted-foreground max-w-md mt-2">
          We couldn't find any products matching your criteria. Try adjusting your filters or search query.
        </p>
      </div>
    );
  }

  return (
    <div 
      ref={gridRef}
      className={`grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-5 lg:gap-6 ${className}`}
    >
      {visibleItems.map((product) => (
        <ProductCard 
          key={product.id}
          {...product}
          tags={[product.category]}
        />
      ))}
      {visibleItems.length < products.length && (
        <div className="col-span-full flex justify-center py-4">
          <div className="animate-pulse flex space-x-2">
            <div className="rounded-full bg-muted h-2 w-2"></div>
            <div className="rounded-full bg-muted h-2 w-2"></div>
            <div className="rounded-full bg-muted h-2 w-2"></div>
          </div>
        </div>
      )}
    </div>
  );
});
